import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/free-solid-svg-icons";

import TableCustom from "../../../component/table/tableCustom";
import TableRowCommon from "../../../component/table/TableRowCommon";
import InputCheckbox from "../../../component/input/inputCheckbox";
import { colors } from "../../../constants/colors";
import { renderStatus } from "../../store/stall-manage/enum/enum";
import { IStall } from "../../../api/zone/zone.api";
import { checkStatusColor, isStallSelectable, paginate } from "./stallPicker.utils";

export interface IStallTableProps {
  /** กรองมาแล้ว ทั้งโซน (ยังไม่ตัดหน้า) */
  rows: IStall[];
  /** 1-based ตาม TableCustom */
  page: number;
  pageLimit: number;
  /** buffer ที่ผู้ใช้ติ๊กไว้ แต่ยังไม่กด "เพิ่มที่เลือก" */
  checkedIds: number[];
  /** แผงที่อยู่ใน dataTable (basket) ของหน้าเพจแล้ว */
  basketIds: number[];
  /** stallTypeId -> ชื่อประเภทแผงค้า เพราะ zone/list ส่งมาแค่ stallTypeId */
  stallTypeNameById: Record<number, string>;
  onToggle: (stallId: number) => void;
  onToggleAllOnPage: (stallIdsOnPage: number[], nextChecked: boolean) => void;
  onPageChange: (page: number) => void;
  onPageLimitChange: (pageLimit: number) => void;
}

export default function StallTable(props: IStallTableProps) {
  const { t } = useTranslation();

  const rowsOnPage = useMemo(
    () => paginate(props.rows, props.page, props.pageLimit),
    [props.rows, props.page, props.pageLimit]
  );

  /** แผงในหน้านี้ที่ยังติ๊กได้จริง — เลือกได้ และยังไม่อยู่ใน basket */
  const eligibleIdsOnPage = useMemo(
    () =>
      rowsOnPage
        .filter((s) => isStallSelectable(s) && !props.basketIds.includes(s.stallId as number))
        .map((s) => s.stallId as number),
    [rowsOnPage, props.basketIds]
  );

  const checkedOnPageCount = eligibleIdsOnPage.filter((id) =>
    props.checkedIds.includes(id)
  ).length;
  const allOnPageChecked =
    eligibleIdsOnPage.length > 0 && checkedOnPageCount === eligibleIdsOnPage.length;
  const someOnPageChecked = checkedOnPageCount > 0 && !allOnPageChecked;

  /** ห้ามใช้ label: 'CHECKBOX' — tableCustom จะห่อด้วย Dropdown แล้ว throw ที่ headCell.values.map
   *  ส่ง ReactNode เป็น label ตรง ๆ แทน (บรรทัด 337 ของ tableCustom รองรับอยู่แล้ว) */
  const headCells = [
    {
      id: "checkAll",
      align: "center",
      width: 60,
      label: (
        <InputCheckbox
          checked={allOnPageChecked}
          indeterminate={someOnPageChecked}
          disabled={eligibleIdsOnPage.length === 0}
          onChange={() => props.onToggleAllOnPage(eligibleIdsOnPage, !allOnPageChecked)}
        />
      ),
    },
    { id: "no", label: "#", align: "center", width: 60 },
    { id: "stallNumber", label: t("MARKET_MAP.HEADCELL.STALL_NUMBER"), align: "center" },
    { id: "stallName", label: t("MARKET_MAP.HEADCELL.STALL"), align: "left" },
    { id: "stallType", label: t("MARKET_MAP.HEADCELL.TYPE_STALL"), align: "left" },
    { id: "status", label: t("MARKET_MAP.HEADCELL.STATUS_STALL"), align: "center", width: 180 },
  ];

  const renderData = (stall: IStall, absoluteIndex: number) => {
    const stallId = stall?.stallId as number;
    /** ลำดับสำคัญ: เช็ค basket ก่อน selectable — ใน edit mode แผงที่ประกาศไปแล้ว
     *  มี status = OPEN ไม่ใช่ BLANK ถ้าสลับลำดับจะขึ้นเป็นแถวจาง unchecked ทั้งที่อยู่ใน basket */
    const inBasket = stallId != null && props.basketIds.includes(stallId);
    const selectable = isStallSelectable(stall);
    const disabled = inBasket || !selectable;
    const checked = inBasket || props.checkedIds.includes(stallId);
    const dimStyle = !inBasket && !selectable ? { opacity: 0.55 } : undefined;

    const objRenderData = {
      key: stallId != null ? `stall-${stallId}` : `stall-new-${absoluteIndex}`,
      id: stallId != null ? `stall-${stallId}` : `stall-new-${absoluteIndex}`,
      obj: stall,
      columns: [
        {
          option: "COMPONENT",
          align: "center",
          width: 60,
          component: (
            <InputCheckbox
              checked={checked}
              disabled={disabled}
              onChange={() => props.onToggle(stallId)}
            />
          ),
        },
        { option: "TEXT", align: "center", label: absoluteIndex + 1, style: dimStyle },
        { option: "TEXT", align: "center", label: stall?.stallNumber || "-", style: dimStyle },
        { option: "TEXT", align: "left", label: stall?.stallName || "-", style: dimStyle },
        {
          option: "TEXT",
          align: "left",
          /** zone/list ไม่ส่ง object stallType มา ต้องแปลงจาก stallTypeId เอง */
          label:
            stall?.stallType?.stallTypeNameTh ||
            props.stallTypeNameById[stall?.stallTypeId as number] ||
            "-",
          style: dimStyle,
        },
        {
          option: "COMPONENT",
          align: "center",
          width: 180,
          component: (
            <span style={dimStyle}>
              <FontAwesomeIcon icon={faCircle} color={checkStatusColor(stall?.status)} />
              <span className="ml-2">{renderStatus(stall?.status || "") || "-"}</span>
              {inBasket && (
                <span className="ml-2" style={{ color: colors.themeMainColor }}>
                  ({t("MARKET_MAP.HEADCELL.SELECTED")})
                </span>
              )}
            </span>
          ),
        },
      ],
    };
    return <TableRowCommon {...objRenderData} />;
  };

  return (
    <TableCustom
      headCells={headCells}
      customScroll
      page={props.page}
      pageLimit={props.pageLimit}
      sortType={"ASC"}
      sortBy=""
      rowCount={props.rows.length}
      tableMinWidth={900}
      rowsData={rowsOnPage.map((stall, index) =>
        renderData(stall, (props.page - 1) * props.pageLimit + index)
      )}
      onSort={() => {}}
      setPage={props.onPageChange}
      setPageLimit={props.onPageLimitChange}
    />
  );
}
